/* MobileTopBar — slim app bar rendered ≤768px in place of the desktop
   sidebar rail. The hamburger opens the nav drawer (the full expanded
   Sidebar); the bell deep-links to /notifications with the same unread
   badge the sidebar shows. Safe-area padding keeps it clear of notches
   when the app runs full-bleed (viewport-fit=cover). */

import { NavLink } from 'react-router-dom';
import { DaedalusWordmark } from '@/components/brand';
import { useUnreadCount } from './useUnreadCount';

export function MobileTopBar({ onMenu }: { onMenu: () => void }) {
  const unread = useUnreadCount();
  return (
    <header className="mtb">
      <button
        type="button"
        className="mtb-menu"
        onClick={onMenu}
        aria-label="Open navigation"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
          <path d="M2 4.5 H16 M2 9 H16 M2 13.5 H16" />
        </svg>
      </button>
      <div className="mtb-brand">
        <DaedalusWordmark size={20} />
      </div>
      <NavLink
        to="/notifications"
        className={({ isActive }) => `mtb-bell ${isActive ? 'on' : ''}`}
        aria-label="Notifications"
      >
        <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25" aria-hidden>
          <path d="M4 7 a4 4 0 0 1 8 0 c0 3 1 4 1 4 H3 s1 -1 1 -4 Z" />
          <path d="M6.5 13 a1.5 1.5 0 0 0 3 0" />
        </svg>
        {unread > 0 && (
          <span className="mtb-badge" aria-label={`${unread} unread`}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </NavLink>
      <style>{`
        .mtb {
          flex-shrink: 0;
          display: flex; align-items: center; gap: 6px;
          padding: env(safe-area-inset-top, 0px) 6px 0;
          min-height: calc(52px + env(safe-area-inset-top, 0px));
          background: var(--bg-2);
          border-bottom: 1px solid var(--rule-soft);
        }
        .mtb-menu {
          display: inline-flex; align-items: center; justify-content: center;
          width: 44px; height: 44px; flex-shrink: 0;
          background: transparent; border: 0; padding: 0;
          color: var(--ink); cursor: pointer;
        }
        .mtb-menu:active { color: var(--accent); }
        .mtb-brand { flex: 1; min-width: 0; display: flex; align-items: center; }
        .mtb-bell {
          position: relative;
          display: inline-flex; align-items: center; justify-content: center;
          width: 44px; height: 44px; flex-shrink: 0;
          color: var(--ink-2); text-decoration: none;
        }
        .mtb-bell.on { color: var(--accent); }
        .mtb-badge {
          position: absolute; top: 6px; right: 4px;
          min-width: 16px; height: 16px; padding: 0 4px; box-sizing: border-box;
          display: inline-flex; align-items: center; justify-content: center;
          background: var(--accent); color: var(--bg-1);
          border-radius: 8px; font-size: 10px; font-weight: 600; line-height: 1;
        }
      `}</style>
    </header>
  );
}
